import React, { ReactNode, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './AdminLayout.css';

interface AdminLayoutProps {
  children: ReactNode;
  activeSection?: string;
}

/** Nome mostrato per l'account admin (sidebar e dropdown operatore) */
const ADMIN_DISPLAY_NAME = 'Denise Cruciani';

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, activeSection: activeSectionProp }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const displayName = isAdmin() ? ADMIN_DISPLAY_NAME : (user?.name || '');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const allMenuItems = [
    {
      id: 'genera-buono',
      label: 'Genera Buono',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M13.3333 5.33301H2.66667C2.29848 5.33301 2 5.63148 2 5.99967V7.33301C2 7.7012 2.29848 7.99967 2.66667 7.99967H13.3333C13.7015 7.99967 14 7.7012 14 7.33301V5.99967C14 5.63148 13.7015 5.33301 13.3333 5.33301Z" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 5.33301V13.9997" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.6668 8V12.6667C12.6668 13.0203 12.5264 13.3594 12.2763 13.6095C12.0263 13.8595 11.6871 14 11.3335 14H4.66683C4.31321 14 3.97407 13.8595 3.72402 13.6095C3.47397 13.3594 3.3335 13.0203 3.3335 12.6667V8" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5.00016 5.33362C4.55814 5.33362 4.13421 5.15803 3.82165 4.84547C3.50909 4.53291 3.3335 4.10899 3.3335 3.66696C3.3335 3.22493 3.50909 2.80101 3.82165 2.48845C4.13421 2.17589 4.55814 2.00029 5.00016 2.00029C5.64329 1.98909 6.27351 2.30113 6.80865 2.89573C7.34379 3.49033 7.75901 4.33989 8.00016 5.33362C8.24131 4.33989 8.65653 3.49033 9.19167 2.89573C9.72681 2.30113 10.357 1.98909 11.0002 2.00029C11.4422 2.00029 11.8661 2.17589 12.1787 2.48845C12.4912 2.80101 12.6668 3.22493 12.6668 3.66696C12.6668 4.10899 12.4912 4.53291 12.1787 4.84547C11.8661 5.15803 11.4422 5.33362 11.0002 5.33362" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/admin/genera-buono',
      adminOnly: false,
    },
    {
      id: 'archivio',
      label: 'Archivio',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M5.3335 1.33301V3.99967" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10.6665 1.33301V3.99967" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.6667 2.66699H3.33333C2.59695 2.66699 2 3.26395 2 4.00033V13.3337C2 14.07 2.59695 14.667 3.33333 14.667H12.6667C13.403 14.667 14 14.07 14 13.3337V4.00033C14 3.26395 13.403 2.66699 12.6667 2.66699Z" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 6.66699H14" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/admin/archivio',
      adminOnly: false,
    },
    {
      id: 'utenti',
      label: 'Utenti',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10.6668 14V12.6667C10.6668 11.9594 10.3859 11.2811 9.88578 10.781C9.38568 10.281 8.70741 10 8.00016 10H4.00016C3.29292 10 2.61464 10.281 2.11454 10.781C1.61445 11.2811 1.3335 11.9594 1.3335 12.6667V14" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M6.00016 7.33333C7.47292 7.33333 8.66683 6.13943 8.66683 4.66667C8.66683 3.19391 7.47292 2 6.00016 2C4.5274 2 3.3335 3.19391 3.3335 4.66667C3.3335 6.13943 4.5274 7.33333 6.00016 7.33333Z" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14.6665 14.0002V12.6669C14.6661 12.0761 14.4694 11.5021 14.1074 11.0351C13.7454 10.5682 13.2386 10.2346 12.6665 10.0869" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10.6665 2.08691C11.2401 2.23378 11.7485 2.56738 12.1116 3.03512C12.4747 3.50286 12.6717 4.07813 12.6717 4.67025C12.6717 5.26236 12.4747 5.83763 12.1116 6.30537C11.7485 6.77311 11.2401 7.10671 10.6665 7.25358" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/admin/utenti',
      adminOnly: true,
    },
    {
      id: 'impostazioni',
      label: 'Template',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8.14667 1.33301H7.85333C7.49971 1.33301 7.16057 1.47348 6.91053 1.72353C6.66048 1.97358 6.52 2.31272 6.52 2.66634V2.78634C6.51976 3.02016 6.45804 3.2498 6.34103 3.45223C6.22401 3.65466 6.05583 3.82277 5.85333 3.93967L5.56667 4.10634C5.36398 4.22337 5.13405 4.28497 4.9 4.28497C4.66595 4.28497 4.43603 4.22337 4.23333 4.10634L4.13333 4.05301C3.82738 3.87652 3.46389 3.82864 3.12267 3.91988C2.78145 4.01112 2.49037 4.23403 2.31333 4.53967L2.16667 4.79301C1.99018 5.09896 1.9423 5.46245 2.03354 5.80367C2.12478 6.1449 2.34769 6.43597 2.65333 6.61301L2.75333 6.67967C2.95485 6.79601 3.12241 6.96307 3.23937 7.16423C3.35632 7.36539 3.4186 7.59366 3.42 7.82634V8.16634C3.42093 8.40129 3.35977 8.63231 3.2427 8.83601C3.12563 9.03972 2.95681 9.20887 2.75333 9.32634L2.65333 9.38634C2.34769 9.56337 2.12478 9.85445 2.03354 10.1957C1.9423 10.5369 1.99018 10.9004 2.16667 11.2063L2.31333 11.4597C2.49037 11.7653 2.78145 11.9882 3.12267 12.0795C3.46389 12.1707 3.82738 12.1228 4.13333 11.9463L4.23333 11.893C4.43603 11.776 4.66595 11.7144 4.9 11.7144C5.13405 11.7144 5.36398 11.776 5.56667 11.893L5.85333 12.0597C6.05583 12.1766 6.22401 12.3447 6.34103 12.5471C6.45804 12.7495 6.51976 12.9792 6.52 13.213V13.333C6.52 13.6866 6.66048 14.0258 6.91053 14.2758C7.16057 14.5259 7.49971 14.6663 7.85333 14.6663H8.14667C8.50029 14.6663 8.83943 14.5259 9.08948 14.2758C9.33953 14.0258 9.48 13.6866 9.48 13.333V13.213C9.48024 12.9792 9.54196 12.7495 9.65898 12.5471C9.77599 12.3447 9.94418 12.1766 10.1467 12.0597L10.4333 11.893C10.636 11.776 10.866 11.7144 11.1 11.7144C11.3341 11.7144 11.564 11.776 11.7667 11.893L11.8667 11.9463C12.1726 12.1228 12.5361 12.1707 12.8773 12.0795C13.2186 11.9882 13.5096 11.7653 13.6867 11.4597L13.8333 11.1997C14.0098 10.8937 14.0577 10.5302 13.9665 10.189C13.8752 9.84778 13.6523 9.55671 13.3467 9.37967L13.2467 9.32634C13.0432 9.20887 12.8744 9.03972 12.7573 8.83601C12.6402 8.63231 12.5791 8.40129 12.58 8.16634V7.83301C12.5791 7.59806 12.6402 7.36704 12.7573 7.16334C12.8744 6.95963 13.0432 6.79048 13.2467 6.67301L13.3467 6.61301C13.6523 6.43597 13.8752 6.1449 13.9665 5.80367C14.0577 5.46245 14.0098 5.09896 13.8333 4.79301L13.6867 4.53967C13.5096 4.23403 13.2186 4.01112 12.8773 3.91988C12.5361 3.82864 12.1726 3.87652 11.8667 4.05301L11.7667 4.10634C11.564 4.22337 11.3341 4.28497 11.1 4.28497C10.866 4.28497 10.636 4.22337 10.4333 4.10634L10.1467 3.93967C9.94418 3.82277 9.77599 3.65466 9.65898 3.45223C9.54196 3.2498 9.48024 3.02016 9.48 2.78634V2.66634C9.48 2.31272 9.33953 1.97358 9.08948 1.72353C8.83943 1.47348 8.50029 1.33301 8.14667 1.33301Z" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/admin/impostazioni',
      adminOnly: true,
    },
    {
      id: 'statistiche',
      label: 'Statistiche',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 2V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H14" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11.3333V6" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8.6665 11.333V3.33301" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5.3335 11.333V9.33301" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      path: '/admin/statistiche',
      adminOnly: true,
    },
  ];

  // Sezione attiva: da prop oppure dall'URL (titolo topbar sempre allineato a dove sei)
  const activeSection = activeSectionProp ?? allMenuItems.find((item) => item.path === pathname)?.id ?? 'dashboard';

  // Filtra i menu items in base al ruolo
  const menuItems = allMenuItems.filter(item => {
    if (item.adminOnly && !isAdmin()) {
      return false;
    }
    return true;
  });

  const handleNavClick = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const currentSectionLabel = allMenuItems.find((item) => item.id === activeSection)?.label ?? 'Menu';

  return (
    <div className="admin-layout">
      {/* Mobile/Tablet: top bar come da design - menu a sinistra, avatar a destra */}
      <header className="admin-mobile-header">
        <div className="admin-topbar-left">
          <button
            type="button"
            className="admin-hamburger"
            onClick={() => setSidebarOpen((o) => !o)}
            aria-label={sidebarOpen ? 'Chiudi menu' : 'Apri menu'}
            aria-expanded={sidebarOpen}
          >
            <span className={sidebarOpen ? 'admin-hamburger-line open' : 'admin-hamburger-line'} />
            <span className={sidebarOpen ? 'admin-hamburger-line open' : 'admin-hamburger-line'} />
            <span className={sidebarOpen ? 'admin-hamburger-line open' : 'admin-hamburger-line'} />
          </button>
          <span className="admin-topbar-title">{currentSectionLabel}</span>
        </div>
        <div className="admin-topbar-right">
          <div className="admin-topbar-avatar" aria-hidden>
            {getInitials(displayName)}
          </div>
        </div>
      </header>

      {/* Overlay quando sidebar aperta su mobile */}
      <div
        className={`admin-sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* User Profile Section */}
        <div className="user-profile-section">
          <div className="user-avatar">
            {getInitials(displayName)}
          </div>
          <div className="user-info">
            <p className="user-greeting">Ciao,</p>
            <p className="user-name">{displayName || user?.name || ''}</p>
            <span className="user-badge">{isAdmin() ? 'Amministratore' : 'Utente'}</span>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="admin-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${activeSection === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item.path)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Logout Button */}
        <div className="logout-section">
          <button className="logout-btn" onClick={() => { handleLogout(); setSidebarOpen(false); }}>
            <span className="logout-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10.6665 11.3337L13.9998 8.00033L10.6665 4.66699" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 8H6" stroke="#4A5565" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
